import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FieldArray, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

import { enums } from "../../constants/enum";
import useSupplierService from "../../services/api/supplierService";
import useSupplyService from "../../services/api/supplyService";
import { supplySchema } from "../../schemas/supply";

import {
  Badge,
  Button,
  IconButton,
  Loading,
  PageTitle,
  Section,
} from "../../components";
import {
  DateInput,
  DropArea,
  SelectInput,
  TextInput,
} from "../../components/forms";
import {
  CloseIcon,
  EditIcon,
  MinusIcon,
  PasteIcon,
  PlusIcon,
} from "../../assets/icons";
import "./SuppliesPage.scss";

function EditSupplyPage() {
  const queryClient = useQueryClient();
  const { supplyId } = useParams();
  const navigate = useNavigate();
  const { getAllSuppliers, queryKey } = useSupplierService();
  const {
    getSupplyOrderById,
    updateSupplyOrder,
    addNewPaymentForSupplyOrder,
    updateSupplyOrderPayment,
    deleteSupplyOrderPayment,
    queryKey: supplyQueryKey,
  } = useSupplyService();

  const [initialValues, setInitialValues] = useState({
    supplier: "",
    items: [{ item: "", quantity: "" }],
    payments: [
      {
        _id: "",
        amount: "",
        date:"",
        method: "",
        paymentSlip: "",
        chequeNumber: "",
      },
    ],
  });

  const { data: supplierData, isLoading: isSuppliersLoading } = useQuery({
    queryKey: [queryKey],
    queryFn: () => getAllSuppliers("", 1, 100),
  });

  const { data: supplyOrder, isLoading: isSupplyLoading } = useQuery({
    queryKey: ["supply-order", supplyId],
    queryFn: () => getSupplyOrderById(supplyId),
    enabled: !!supplyId,
  });

  useEffect(() => {
    if (supplyOrder) {
      setInitialValues({
        // Extract Supplier ID from populated object or use directly if ID
        supplier:
          supplyOrder.payload?.supplier?._id ||
          supplyOrder.supplier?._id ||
          supplyOrder.supplier ||
          "",

        // Map items to get item ID and quantity
        items: (supplyOrder.payload?.items || supplyOrder.items || []).map(
          (i) => ({
            item: i._id, // Provided JSON shows item details in array, we assume _id is the Item ID
            quantity: i.quantity,
          }),
        ),

        // Map payments, flattening paymentSlip object to just ID
        payments: (
          supplyOrder.payload?.payments ||
          supplyOrder.payments ||
          []
        ).map((p) => ({
          _id: p._id,
          amount: p.amount || "",
          date: p.date.split("T")[0],
          method: p.method || enums.PAYMENT_METHODS.CASH,
          paymentSlip: p.paymentSlip?._id || p.paymentSlip || "",
          chequeNumber: p.chequeNumber || "",
        })),
      });
    }
  }, [supplyOrder]);

  const mutation = useMutation({
    mutationFn: async (values) => {
      // 1. Prepare main order payload
      const mainOrderPayload = {
        supplier: values.supplier,
        items: values.items.map((i) => ({
          item: i.item,
          quantity: Number(i.quantity),
        })),
      };

      // A. Update Main Order FIRST explicitly to avoid version conflicts
      // We await this to ensure the main document version is updated before sub-docs are touched.
      await updateSupplyOrder({
        id: supplyId,
        payload: mainOrderPayload,
      });

      // B. Handle Payments (Add / Update / Delete)
      const originalPayments =
        supplyOrder?.payload?.payments || supplyOrder?.payments || [];
      const currentPayments = values.payments;

      // Identify Deleted Payments
      const currentids = new Set(
        currentPayments.map((p) => p._id).filter((id) => id),
      );

      // Execute Deletes Sequentially
      for (const p of originalPayments) {
        if (!currentids.has(p._id)) {
          await deleteSupplyOrderPayment({
            id: supplyId,
            paymentId: p._id,
          });
        }
      }

      // Execute Adds and Updates Sequentially
      for (const p of currentPayments) {
        const paymentPayload = {
          amount: Number(p.amount),
          date: p.date,
          method: p.method,
          paymentSlip: p.paymentSlip, // Should be ID string from form state
        };
        if (p.method === enums.PAYMENT_METHODS.CHEQUE) {
          paymentPayload.chequeNumber = p.chequeNumber;
        }

        if (p._id) {
          // Update Existing Payment
          await updateSupplyOrderPayment({
            id: supplyId,
            paymentId: p._id,
            payload: paymentPayload,
          });
        } else {
          // Add New Payment
          await addNewPaymentForSupplyOrder({
            id: supplyId,
            payload: paymentPayload,
          });
        }
      }
    },
    onSuccess: async () => {
      toast.success("Supply record updated successfully");
      await queryClient.resetQueries([supplyQueryKey]);
      navigate("/supplies");
    },
    onError: (error) => {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Failed to update supply record",
      );
    },
  });

  if (isSuppliersLoading || isSupplyLoading) {
    return <Loading variant="fullScreen" />;
  }

  const supplierOptions = [
    { label: "Select Supplier", value: "", disabled: true },
    ...(supplierData?.suppliers || []).map((supplier) => ({
      label: supplier.name,
      value: supplier._id,
    })),
  ];

  const handlePaste = async (setFieldValue, fieldPath) => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setFieldValue(fieldPath, text);
      }
    } catch (err) {
      toast.error("Failed to read clipboard");
    }
  };

  return (
    <div className="page add-supply-page">
      <PageTitle text="Edit Supply Record" prev="/supplies" />
      <Section className="supply-form">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={supplySchema}
          onSubmit={(values) => mutation.mutate(values)}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <SelectInput
                name="supplier"
                label="Supplier Name"
                values={supplierOptions}
              />

              <FieldArray name="items">
                {({ push, remove }) => (
                  <>
                    {values.items.map((item, index) => (
                      <Section key={index} className="item-input-area">
                        <div className="label-are">
                          <Badge text={`Item ${index + 1}`} />
                          {values.items.length > 1 && (
                            <Badge
                              icon={<CloseIcon className="icon" />}
                              onClick={() => remove(index)}
                              style={{ cursor: "pointer" }}
                            />
                          )}
                        </div>
                        <div className="item-code">
                          <TextInput
                            name={`items[${index}].item`}
                            label="Item Code"
                            placeholder="Type here"
                            className="item-code-input"
                          />
                          <Button
                            text="Paste"
                            type="button"
                            className="paste-btn"
                            icon={<PasteIcon />}
                            onClick={() =>
                              handlePaste(setFieldValue, `items[${index}].item`)
                            }
                          />
                        </div>
                        <div className="item-qty">
                          <IconButton
                            icon={<PlusIcon />}
                            className="action-btn"
                            rounded
                            onClick={() =>
                              setFieldValue(
                                `items[${index}].quantity`,
                                (Number(item.quantity) || 0) + 1,
                              )
                            }
                          />
                          <TextInput
                            type="number"
                            name={`items[${index}].quantity`}
                            className="qty-input"
                            placeholder="00"
                          />
                          <IconButton
                            icon={<MinusIcon />}
                            className="action-btn"
                            rounded
                            onClick={() =>
                              setFieldValue(
                                `items[${index}].quantity`,
                                Math.max(0, (Number(item.quantity) || 0) - 1),
                              )
                            }
                          />
                        </div>
                      </Section>
                    ))}
                    <Button
                      text="Add Item"
                      type="button"
                      className="add-new-item-btn"
                      icon={<PlusIcon />}
                      onClick={() => push({ item: "", quantity: "" })}
                    />
                  </>
                )}
              </FieldArray>

              <FieldArray name="payments">
                {({ push, remove }) => (
                  <>
                    {values.payments.map((payment, index) => (
                      <Section key={index}>
                        <div className="payment-input-area">
                          <div className="label-are">
                            <Badge text={`Payment ${index + 1}`} />
                            {index > 0 && (
                              <Badge
                                icon={<CloseIcon className="icon" />}
                                onClick={() => remove(index)}
                                style={{ cursor: "pointer" }}
                              />
                            )}
                          </div>
                          <div className="input-group">
                            <div className="amount-area">
                              <span>LKR</span>
                              <TextInput
                                label="Amount"
                                type="currency"
                                name={`payments[${index}].amount`}
                                placeholder="Type here"
                                className="currency-input-field"
                              />
                            </div>
                            <DateInput
                              name={`payments[${index}].date`}
                              label="Date"
                            />
                          </div>
                        </div>
                        <SelectInput
                          name={`payments[${index}].method`}
                          label="Payment Method"
                          className="payment-method"
                          values={[
                            {
                              label: "Select Payment Method",
                              value: "",
                              disabled: true,
                            },
                            ...Object.values(enums.PAYMENT_METHODS).map(
                              (method) => ({
                                label: method,
                                value: method,
                              }),
                            ),
                          ]}
                        />
                        {payment.method === enums.PAYMENT_METHODS.CHEQUE && (
                          <TextInput
                            name={`payments[${index}].chequeNumber`}
                            label="Cheque Number"
                            placeholder="Enter Cheque Number"
                            className="cheque-input"
                          />
                        )}
                        <DropArea
                          name={`payments[${index}].paymentSlip`}
                          label="Payment Slip"
                          isFileUpdate={true}
                          fileId={payment.paymentSlip}
                        />
                      </Section>
                    ))}
                    <Button
                      text="Add Payment"
                      type="button"
                      className="add-new-item-btn"
                      icon={<PlusIcon />}
                      onClick={() =>
                        push({
                          _id: "",
                          amount: "",
                          date: new Date(),
                          method: enums.PAYMENT_METHODS.CASH,
                          paymentSlip: "",
                          chequeNumber: "",
                        })
                      }
                    />
                  </>
                )}
              </FieldArray>

              <Button
                text="Update Supply Record"
                type="submit"
                icon={<EditIcon />}
                className="submit-button"
              />
            </Form>
          )}
        </Formik>
      </Section>
      <Loading variant="fullScreen" isLoading={mutation.isPending} />
    </div>
  );
}

export default EditSupplyPage;
